import { useCallback, useEffect, useState } from "react";
import api, { describeFailure, isTransient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import fe from "@/version.json";

const INTERVAL_MS = 15 * 60 * 1000;
const RETRY_MS = [1000, 2000, 4000];
const unreachable = (err) => ({ ok: false, blocking: false, checks: [{ name: "Self-check request", ok: false, detail: `Backend unreachable — ${describeFailure(err)}. Retried ${RETRY_MS.length}×; will retry shortly.` }], version: "?" });
const plural = (n, word) => `${n} ${word}${n > 1 ? "s" : ""}`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function postSelfCheck(body) {
  for (let i = 0; ; i++) {
    try { return (await api.post("/system/self-check", body)).data; }
    catch (err) { if (!isTransient(err) || i >= RETRY_MS.length) throw err; await sleep(RETRY_MS[i]); }
  }
}

function useSelfCheck() {
  const { user, activeWorkspaceId } = useAuth();
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (trigger) => {
    setBusy(true);
    try { setRes(await postSelfCheck({ frontend_version: fe.version, workspace_id: activeWorkspaceId, trigger })); }
    catch (err) { if (err?.response?.status !== 401) setRes(unreachable(err)); }
    finally { setBusy(false); }
  }, [activeWorkspaceId]);
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    run("login");
    const t = setInterval(() => run("interval"), INTERVAL_MS);
    return () => clearInterval(t);
  }, [userId, run]);
  return { res, busy, run };
}

function BlockingOverlay({ failed }) {
  return (
    <div className="fixed inset-0 z-[70] bg-slate-950/95 flex items-center justify-center p-6" data-testid="system-check-blocking">
      <div className="max-w-md text-center space-y-4">
        <ShieldAlert className="w-10 h-10 text-red-400 mx-auto" />
        <h2 className="font-heading text-xl text-slate-100">Update required</h2>
        <p className="text-sm text-slate-300">{failed.map((c) => c.detail).join(" · ")}</p>
        <p className="text-xs text-slate-500">The app you have open does not match the running server version. Reload to get the current version and spec.</p>
        <button onClick={() => window.location.reload()} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="system-check-reload-button"><RefreshCw className="w-4 h-4" /> Reload now</button>
      </div>
    </div>
  );
}

function CheckDialog({ open, setOpen, res, busy, run, showDetails }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-lg" data-testid="system-check-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">{res.ok ? <ShieldCheck className="w-4 h-4 text-emerald-400" /> : <ShieldAlert className="w-4 h-4 text-red-400" />} System self-check</DialogTitle></DialogHeader>
        <div className="text-[11px] font-mono text-slate-500">v{res.version} · build {res.commit} · spec {res.spec_revision} · checked {res.checked_at ? new Date(res.checked_at).toLocaleString() : ""}</div>
        <div className="space-y-1 text-xs">
          {(res.checks || []).map((c) => (
            <div key={c.name} className="flex gap-2 items-start" data-testid={`system-check-item-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <span className={c.ok ? "text-emerald-400" : "text-red-400"}>{c.ok ? "✓" : "✗"}</span>
              <div><span className="text-slate-200">{c.name}</span>{(showDetails || !c.ok) && <div className="text-slate-500">{c.detail}</div>}</div>
            </div>
          ))}
        </div>
        <div className="flex justify-end"><button onClick={() => run("manual")} disabled={busy} className="border border-slate-700 hover:border-amber-500 px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5" data-testid="system-check-rerun-button"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Run again</button></div>
      </DialogContent>
    </Dialog>
  );
}

export function SystemCheck() {
  const { user, currentRole } = useAuth();
  const { res, busy, run } = useSelfCheck();
  const [open, setOpen] = useState(false);
  if (!user || !res) return null;
  const failed = (res.checks || []).filter((c) => !c.ok);
  const pillTone = res.ok ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/5" : "border-red-500/50 text-red-300 hover:bg-red-500/5";
  return (
    <>
      <button onClick={() => setOpen(true)} className={`w-full flex items-center gap-2 text-[11px] font-mono px-3 py-1.5 rounded border ${pillTone}`} title="System self-check (runs at sign-in and every 15 min)" data-testid="system-check-pill">
        {res.ok ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />} {res.ok ? "System OK" : `${plural(failed.length, "check")} failed`} · v{res.version}
      </button>
      {!res.ok && !res.blocking && (
        <div className="fixed top-0 inset-x-0 z-[60] bg-red-600/95 text-white text-xs px-4 py-2 flex items-center gap-3 justify-center" data-testid="system-check-banner">
          <ShieldAlert className="w-4 h-4" /> Self-check found {plural(failed.length, "issue")}: {failed.map((c) => c.name).join(", ")}.
          <button onClick={() => setOpen(true)} className="underline" data-testid="system-check-details-button">Details</button>
        </div>
      )}
      {res.blocking && <BlockingOverlay failed={failed} />}
      <CheckDialog open={open} setOpen={setOpen} res={res} busy={busy} run={run} showDetails={currentRole === "admin"} />
    </>
  );
}
